import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import {
  ArrowLeft,
  BookOpen,
  Calculator,
  FlaskConical,
  Users,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  Layers,
  FileText,
  Sparkles,
  Loader2,
  Check,
  Circle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { QHubFooter } from "@/components/QHubBranding";

const subjectConfig = {
  "comunicacion-lenguaje": {
    name: "Comunicacion y Lenguaje",
    icon: BookOpen,
    color: "#ef4444",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
  "ciencia-tecnologia": {
    name: "Ciencia y Tecnologia",
    icon: FlaskConical,
    color: "#14b8a6",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
  },
  "matematica-datos": {
    name: "Matematica y Datos",
    icon: Calculator,
    color: "#8b5cf6",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
  },
  "ciudadania-valores": {
    name: "Ciudadania y Valores",
    icon: Users,
    color: "#f59e0b",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
  },
};

const bloomColorMap = {
  Recordar: "#94a3b8",
  Comprender: "#60a5fa",
  Aplicar: "#34d399",
  Analizar: "#fbbf24",
  Evaluar: "#f97316",
  Crear: "#a78bfa",
};

const LearningPaths = () => {
  const { subject } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUnit, setExpandedUnit] = useState(null);

  const config = subjectConfig[subject] || subjectConfig["comunicacion-lenguaje"];
  const Icon = config.icon;
  const gradeLabel = user?.grade || "8";

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await axios.get(
          `${API}/curriculum/${gradeLabel}/${subject}/units-detail`,
          { withCredentials: true }
        );
        setUnits(response.data || []);
      } catch (error) {
        console.error("Error fetching units-detail:", error);
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUnits();
  }, [subject, gradeLabel]);

  const handleSelectContent = (unit, contentName) => {
    navigate(`/tutor/${subject}`, {
      state: {
        unitCode: unit.unit,
        unitName: unit.unit,
        preselectedTopic: contentName,
      },
    });
  };

  const handleStartUnit = (unit) => {
    navigate(`/tutor/${subject}`, {
      state: {
        unitCode: unit.unit,
        unitName: unit.unit,
      },
    });
  };

  const toggleExpand = (unitName) => {
    setExpandedUnit((prev) => (prev === unitName ? null : unitName));
  };

  const totalIndicators = units.reduce((sum, u) => sum + u.indicator_count, 0);
  const totalCompleted = units.reduce((sum, u) => sum + (u.completed_indicators || 0), 0);
  const progressPercent = totalIndicators > 0 ? Math.round((totalCompleted / totalIndicators) * 100) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]" data-testid="learning-paths-page">
      {/* Header */}
      <header className={`${config.bgColor} border-b ${config.borderColor}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            className="text-gray-600 hover:text-gray-900 hover:bg-white/50 mb-4 -ml-2"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver a la red
          </Button>

          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ backgroundColor: config.color }}
            >
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1
                className="text-2xl md:text-3xl font-bold font-['Outfit'] text-gray-900"
                data-testid="subject-title"
              >
                {config.name}
              </h1>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <GraduationCap className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Grado {gradeLabel}</span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600" data-testid="units-count">
                    {units.length} unidades
                  </span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600" data-testid="indicators-count">
                    {totalIndicators} indicadores
                  </span>
                </div>
              </div>
              {totalCompleted > 0 && (
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex-1 max-w-[200px] h-2 bg-white/60 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${progressPercent}%`, backgroundColor: config.color }}
                      data-testid="overall-progress-bar"
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-600" data-testid="overall-progress-text">
                    {totalCompleted}/{totalIndicators} completados ({progressPercent}%)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2
              className="w-10 h-10 animate-spin"
              style={{ color: config.color }}
            />
          </div>
        ) : units.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <Layers className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No hay unidades disponibles
            </h3>
            <p className="text-gray-500 mb-6">
              Aun no se ha cargado el curriculo para este grado y materia.
            </p>
            <Button
              onClick={() =>
                navigate(`/tutor/${subject}`, {
                  state: { unitCode: null, unitName: null },
                })
              }
              className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90 text-white rounded-xl px-8"
              data-testid="start-chat-anyway-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Iniciar chat libre con QHUB.BUDDY
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3" data-testid="units-list">
            {units.map((unit, index) => {
              const isExpanded = expandedUnit === unit.unit;
              const unitCompleted = unit.completed_indicators || 0;
              const unitTotal = unit.total_indicators || unit.indicator_count;
              const unitProgress = unitTotal > 0 ? Math.round((unitCompleted / unitTotal) * 100) : 0;
              const unitDone = unitProgress === 100 && unitTotal > 0;
              return (
                <motion.div
                  key={unit.unit}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${unitDone ? "border-green-200" : "border-gray-100"}`}
                  data-testid={`unit-card-${index}`}
                >
                  {/* Unit Header */}
                  <button
                    onClick={() => toggleExpand(unit.unit)}
                    className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50/50 transition-colors"
                    data-testid={`unit-toggle-${index}`}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: unitDone ? "#dcfce7" : `${config.color}12` }}
                    >
                      {unitDone ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <span
                          className="text-lg font-bold"
                          style={{ color: config.color }}
                        >
                          {index + 1}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-[15px] leading-snug">
                        {unit.unit}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{unit.indicator_count} indicadores</span>
                        {unit.content_count > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>{unit.content_count} contenidos</span>
                          </>
                        )}
                        {unitCompleted > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className={unitDone ? "text-green-600 font-medium" : "text-gray-500"} data-testid={`unit-progress-${index}`}>
                              {unitCompleted}/{unitTotal} completados
                            </span>
                          </>
                        )}
                      </div>
                      {unitCompleted > 0 && (
                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden w-full max-w-[240px]">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${unitProgress}%`,
                              backgroundColor: unitDone ? "#22c55e" : config.color,
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div
                        className="transition-transform duration-200"
                        style={{
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      >
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </button>

                  {/* Expanded Contents */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 border-t border-gray-100">
                          {unit.contents.length > 0 ? (
                            <div className="mt-4 space-y-2" data-testid={`unit-contents-${index}`}>
                              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                                Contenidos conceptuales
                              </p>
                              {unit.contents.map((content, ci) => (
                                <button
                                  key={ci}
                                  onClick={() => handleSelectContent(unit, content.name)}
                                  className={`w-full flex items-start gap-3 p-3 rounded-xl text-left border transition-all group ${
                                    content.is_completed
                                      ? "border-green-200 bg-green-50/30 hover:bg-green-50/60"
                                      : content.is_started
                                        ? "border-blue-200 bg-blue-50/20 hover:bg-blue-50/40"
                                        : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/80"
                                  }`}
                                  data-testid={`content-${index}-${ci}`}
                                >
                                  <div
                                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                                  >
                                    {content.is_completed ? (
                                      <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center">
                                        <Check className="w-4 h-4 text-green-600" />
                                      </div>
                                    ) : content.is_started ? (
                                      <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                                        <Circle className="w-3.5 h-3.5 text-blue-500 fill-blue-500" />
                                      </div>
                                    ) : (
                                      <div
                                        className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-semibold"
                                        style={{
                                          backgroundColor: `${config.color}10`,
                                          color: config.color,
                                        }}
                                      >
                                        {ci + 1}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-800 leading-relaxed">
                                      {content.name}
                                    </p>
                                    {content.bloom_levels.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-2">
                                        {content.bloom_levels.map((bl) => (
                                          <span
                                            key={bl}
                                            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                                            style={{
                                              backgroundColor: `${bloomColorMap[bl] || "#94a3b8"}18`,
                                              color: bloomColorMap[bl] || "#94a3b8",
                                            }}
                                          >
                                            {bl}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0 mt-1" />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4 text-center py-4">
                              <p className="text-sm text-gray-400 mb-3">
                                No hay contenidos detallados para esta unidad.
                              </p>
                            </div>
                          )}

                          {/* Start unit button */}
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <Button
                              onClick={() => handleStartUnit(unit)}
                              className="w-full text-white rounded-xl h-11"
                              style={{ backgroundColor: config.color }}
                              data-testid={`start-unit-btn-${index}`}
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Iniciar tutor en esta unidad
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      <QHubFooter />
    </div>
  );
};

export default LearningPaths;
